import React, { useState, useEffect } from 'react';
import { makeStyles, Drawer, Box, IconButton, Grid, Tooltip } from '@material-ui/core';
import { Chip, Typography as SwagupTypography } from '@swagup-com/components';
import CloseIcon from '@material-ui/icons/Close';
import { Info } from '@material-ui/icons';
import DeleteIcon from '@material-ui/icons/Delete';
// import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { useResponsive } from '../../hooks';
import { CommonButton } from './commonButtons';
import { RightArrowIcon } from '../icons';
import { DollarPriceSymbol } from './common';
import { shoppingExperienceColors } from '../constants';
import { useCartContext } from '../../context/cart.context';
import { getThumbnailURLs } from '../helper';
import PackBuilderIntroModal from './packBuilderIntroModal';
// import fireHazard from '../../assets/images/cart/fire-hazard.png';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: '24px',
    height: '268px',
    display: 'flex',
    justifyContent: 'space-between',
    boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.12), 0px 12px 24px 2px rgba(0, 0, 0, 0.06)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  packDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '25px',
    height: '150px',
    padding: '0px 20px'
  },
  packImages: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  imageContainer: {
    position: 'relative',
    width: '100px',
    height: '100px',
    '&:hover $removeIcon': {
      visibility: 'visible'
    }
  },
  packPricing: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '350px',
    height: '150px'
  },
  pricingText: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  packQuantities: {
    padding: '10px 20px',
    border: `1px solid ${shoppingExperienceColors.primary}`,
    borderRadius: '4px'
  },
  ctaButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'column',
    width: '260px',
    height: '120px'
  },
  image: {
    width: '100px',
    height: '100px',
    objectFit: 'contain'
  },
  removeIcon: {
    position: 'absolute',
    top: 0,
    left: 0,
    visibility: 'hidden',
    zIndex: 1,
    color: shoppingExperienceColors?.ctaHover,
    '&:hover': {
      cursor: 'pointer',
      color: shoppingExperienceColors?.error
    }
  },
  countChip: {
    position: 'absolute',
    top: 0,
    right: 4,
    zIndex: 1,
    fontWeight: 600,
    backgroundColor: shoppingExperienceColors.primary,
    color: shoppingExperienceColors.white,
    borderRadius: '50%',
    height: '24px',
    width: '24px',
    boxSizing: 'border-box',
    '& .MuiChip-label': {
      fontFamily: 'Inter',
      padding: 0,
      fontSize: '14px',
      lineHeight: '24px',
      textAlign: 'center'
    }
  },
  overflowCount: {
    fontSize: '16px',
    fontFamily: 'Inter',
    fontWeight: 600,
    lineHeight: '24px',
    width: '100px',
    height: '100px',
    backgroundColor: shoppingExperienceColors.blueLightHovered,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: shoppingExperienceColors.black,
    borderRadius: '5px'
  },
  customTooltip: {
    fontFamily: 'Inter',
    fontSize: '14px'
  },
  maxIcon: {
    position: 'absolute',
    top: 25,
    left: -3,
    zIndex: 1,
    color: shoppingExperienceColors.warning
  },
  hazmatIcon: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    zIndex: 1,
    color: shoppingExperienceColors.error
  },
  fireHazard: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    zIndex: 1,
    height: 24,
    width: 24
  }
}));

const SwagPackDrawer = ({ open, onClose }) => {
  const classes = useStyles();
  const { isTablet } = useResponsive();
  const { packItems, removeCartItem, updateCart } = useCartContext();
  const images = getThumbnailURLs(packItems);
  const maxVisible = isTablet ? 2 : 5;
  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  useEffect(() => {
    if (open && packItems?.length === 0) {
      onClose();
    }
  }, [packItems, open, onClose]);

  const itemsWithImages = packItems.map((item, idx) => ({
    ...item,
    url: images[idx]?.url,
    isDefault: images[idx]?.isDefault
  }));

  const visibleItems = itemsWithImages.slice(0, maxVisible);
  const remainingCount = itemsWithImages?.length - maxVisible;

  const handleRemoveSingleSwagPackItem = swagPack => {
    const { cartId, quantity } = swagPack;
    if (quantity > 1) {
      updateCart(cartId, quantity - 1);
    } else {
      removeCartItem(cartId);
    }
  };

  const SwagPackImages = () => {
    return (
      <Box className={classes.packImages} flexGrow={1}>
        {visibleItems.map((item, idx) => (
          <Box key={item.cartId} className={classes.imageContainer}>
            <DeleteIcon
              fontSize="small"
              className={classes.removeIcon}
              onClick={() => handleRemoveSingleSwagPackItem(item)}
            />
            {/* {item?.quantity === 10 && (
              <Tooltip
                placement="top"
                arrow
                title="The maximum quantity per pack has been reached; a single item may only have a maximum quantity of 10 per pack."
                classes={{ tooltip: classes.customTooltip }}
              >
                <ErrorOutlineIcon className={classes.maxIcon} />
              </Tooltip>
            )}
            {item?.isHazmat && (
              <Tooltip
                placement="top"
                arrow
                title="No more than one hazmat item may be included in a pack."
                classes={{ tooltip: classes.customTooltip }}
              >
                <img src={fireHazard} alt="Fire-hazard" className={classes.fireHazard} />
              </Tooltip>
            )} */}
            {item?.quantity > 1 && <Chip label={item?.quantity} className={classes.countChip} />}
            {item?.isDefault ? (
              <Tooltip title="Thumbnail image is not found">
                <img src={item.url} alt={`Thumbnail ${idx + 1}`} className={classes.image} />
              </Tooltip>
            ) : (
              <img src={item.url} alt={`Thumbnail ${idx + 1}`} className={classes.image} />
            )}
          </Box>
        ))}

        {remainingCount > 0 && <Box className={classes.overflowCount}>+{remainingCount}</Box>}
      </Box>
    );
  };

  const SwagPackPricing = () => {
    return (
      <Box className={classes.packPricing}>
        <Grid className={classes.pricingText}>
          <Grid style={{ padding: '10px 0px' }}>
            <SwagupTypography variant="body1SemiBoldInter">Pack quantity (Min 25)</SwagupTypography>
          </Grid>
          <Grid className={classes.packQuantities}>
            <SwagupTypography variant="body1SemiBoldInter" style={{ color: shoppingExperienceColors.primary }}>
              100
            </SwagupTypography>
          </Grid>
        </Grid>
        <Grid className={classes.pricingText}>
          <Grid style={{ padding: '10px 0px' }}>
            <Grid style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SwagupTypography variant="body3RegularInter">Price per pack</SwagupTypography>
            </Grid>
          </Grid>
          <Grid style={{ paddingTop: '10px' }}>
            <SwagupTypography variant="body3RegularInter">{DollarPriceSymbol(1231.123)}</SwagupTypography>
          </Grid>
        </Grid>
        <Grid
          className={classes.pricingText}
          style={{ borderTop: `1px solid ${shoppingExperienceColors.borderLineGray}` }}
        >
          <Grid style={{ padding: '10px 0px' }}>
            <Grid style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SwagupTypography variant="body3RegularInter" style={{ color: shoppingExperienceColors.primary }}>
                Membership Discounts
              </SwagupTypography>
            </Grid>
          </Grid>
          <Grid style={{ padding: '10px 0px' }}>
            <SwagupTypography variant="body3RegularInter" style={{ color: shoppingExperienceColors.primary }}>
              -{DollarPriceSymbol(200.123)}
            </SwagupTypography>
          </Grid>
        </Grid>
        <Grid className={classes.pricingText}>
          <Grid style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingBottom: '10px' }}>
            <SwagupTypography variant="body3SemiBoldInter">Total pack price estimate</SwagupTypography>
            <Tooltip
              placement="top"
              title="This estimate is based on standard decoration with a single-color logo. Final pricing may vary depending on your logo, decoration method, taxes, and shipping details."
              arrow
              classes={{
                tooltip: classes.customTooltip
              }}
            >
              <Info style={{ fontSize: 18, color: shoppingExperienceColors.black }} />
            </Tooltip>
          </Grid>
          <Grid style={{ paddingBottom: '10px' }}>
            <SwagupTypography variant="body3SemiBoldInter">{DollarPriceSymbol(123123)}</SwagupTypography>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const SwagPackCTA = ({ onContinueShopping }) => {
    return (
      <Box className={classes.ctaButtons}>
        <CommonButton
          text="Build your Swag Pack"
          mr={`${0} !important`}
          mt={`${0} !important`}
          isActive
          hasIcon
          onClick={handleOpenModal}
          icon={<RightArrowIcon color={shoppingExperienceColors.white} />}
        />
        <CommonButton
          text="Continue shopping"
          mr={`${0} !important`}
          isActive
          onClick={onContinueShopping}
          outlined
          hasIcon
          icon={<RightArrowIcon />}
        />
      </Box>
    );
  };

  const handleDrawerClose = (event, reason) => {
    if (reason === 'backdropClick') return;
    onClose(event, reason);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleDrawerClose}
      classes={{ paper: classes.paper }}
      ModalProps={{
        disableScrollLock: true,
        style: { pointerEvents: 'none' },
        BackdropProps: {
          style: { backgroundColor: 'transparent' }
        }
      }}
      PaperProps={{
        style: { pointerEvents: 'auto' }
      }}
    >
      <Box className={classes.header}>
        <SwagupTypography variant="h5SemiBoldInter">Items in your Swag Pack</SwagupTypography>
        <IconButton onClick={onClose}>
          <CloseIcon style={{ color: shoppingExperienceColors.black }} />
        </IconButton>
      </Box>

      <Box className={classes.packDetails}>
        <SwagPackImages />
        <SwagPackPricing />
        <SwagPackCTA onContinueShopping={handleDrawerClose} />
      </Box>

      <PackBuilderIntroModal open={openModal} onClose={handleCloseModal} />
    </Drawer>
  );
};

export default SwagPackDrawer;
